import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X, Scale, GripVertical, FolderTree, FileSearch } from 'lucide-react';
import HSCode from '../common/HSCode';
import TariffPanel from './TariffPanel';
import AnalyticsCardPanel from './AnalyticsCardPanel';

const MIN_WIDTH = 280;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 380;
const STORAGE_KEY = 'tariffRailWidth';

function loadStoredWidth() {
  if (typeof window === 'undefined') return DEFAULT_WIDTH;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= MIN_WIDTH && n <= MAX_WIDTH ? n : DEFAULT_WIDTH;
}

// Compute how wide TariffRail can grow without forcing flex siblings below
// their min-widths. Returns a value in [MIN_WIDTH, MAX_WIDTH].
function computeMaxAllowed(aside) {
  const parent = aside?.parentElement;
  if (!parent) return MAX_WIDTH;
  const parentWidth = parent.clientWidth;
  let othersFloor = 0;
  for (const sib of parent.children) {
    if (sib === aside) continue;
    const cs = window.getComputedStyle(sib);
    // shrink-0 siblings stay at their current width; shrinkable ones can shrink to min-width.
    if (parseFloat(cs.flexShrink) === 0) {
      othersFloor += sib.offsetWidth;
    } else {
      const minW = parseFloat(cs.minWidth);
      othersFloor += Number.isFinite(minW) && minW > 0 ? minW : sib.offsetWidth;
    }
  }
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, parentWidth - othersFloor));
}

export default function TariffRail({
  open,
  code,
  originalCode,
  onClose,
  onTariffInfo,
  onCountChange,
}) {
  const [width, setWidth] = useState(loadStoredWidth);
  const [activeTab, setActiveTab] = useState('tree');
  const [activeCode, setActiveCode] = useState(code);
  const [analyticsCount, setAnalyticsCount] = useState(null);
  const asideRef = useRef(null);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const lastWidthRef = useRef(width);
  lastWidthRef.current = width;

  useEffect(() => {
    setActiveCode(code);
    setAnalyticsCount(null);
  }, [code]);

  const handleResizeStart = useCallback((e) => {
    draggingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = lastWidthRef.current;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, []);

  useEffect(() => {
    function onMove(e) {
      if (!draggingRef.current) return;
      // Rail is right-anchored; dragging cursor left widens the rail.
      const delta = startXRef.current - e.clientX;
      const dynMax = computeMaxAllowed(asideRef.current);
      const desired = startWidthRef.current + delta;
      const next = Math.max(MIN_WIDTH, Math.min(dynMax, desired));
      setWidth(next);
    }
    function onUp() {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        window.localStorage.setItem(STORAGE_KEY, String(lastWidthRef.current));
      } catch {}
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Clamp on TariffRail mount (covers the initial localStorage → state path).
  useLayoutEffect(() => {
    if (!asideRef.current) return;
    const dynMax = computeMaxAllowed(asideRef.current);
    if (lastWidthRef.current > dynMax) {
      setWidth(dynMax);
    }
  }, [open, code]);

  // Reclamp on:
  //   - parent resize (window resize, filters toggle)
  //   - parent's childList change (sibling panels mount/unmount — e.g. SimilarRail opens)
  // Deferred to rAF so we read sibling widths AFTER browser layout settles.
  useEffect(() => {
    const aside = asideRef.current;
    if (!aside?.parentElement) return;
    const parent = aside.parentElement;

    const recheck = () => {
      if (draggingRef.current) return;
      requestAnimationFrame(() => {
        if (!asideRef.current) return;
        const dynMax = computeMaxAllowed(asideRef.current);
        if (lastWidthRef.current > dynMax) {
          setWidth(dynMax);
        }
      });
    };

    const ro = new ResizeObserver(recheck);
    ro.observe(parent);
    const mo = new MutationObserver(recheck);
    mo.observe(parent, { childList: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  if (!open || !code) return null;
  const navigated = code !== originalCode;

  return (
    <aside
      ref={asideRef}
      className="shrink-0 flex flex-col border-l border-border-default bg-surface animate-slide-in-right relative"
      style={{ width }}
    >
      {/* Drag handle: wider hit area straddling the left border, with always-visible grip */}
      <div
        onMouseDown={handleResizeStart}
        role="separator"
        aria-orientation="vertical"
        aria-label="Зміна ширини панелі"
        className="absolute top-0 bottom-0 z-20 cursor-col-resize group hover:bg-blue-500/5 active:bg-blue-500/10 transition-colors"
        style={{ left: -4, width: 8 }}
        title="Перетягнути для зміни ширини"
      >
        {/* Vertical accent line at the rail's left edge, brighter on hover/active */}
        <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-transparent group-hover:bg-blue-400 group-active:bg-blue-500 transition-colors" />
        {/* Persistent grip badge in vertical centre */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none rounded-md border border-border-default bg-surface p-0.5 shadow-sm transition-colors group-hover:border-blue-400 group-hover:bg-blue-50 dark:group-hover:bg-blue-950/40">
          <GripVertical className="w-3 h-3 text-text-disabled group-hover:text-blue-500 transition-colors block" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-b border-border-default px-4 py-2.5 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Scale className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
          <span className="text-sm font-semibold text-text-primary">Тарифне дерево</span>
          <HSCode code={code} size="sm" />
          {navigated && (
            <span
              className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-1.5 h-[18px] text-[10px] font-medium text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/40 dark:text-blue-300"
              title={`Початково — ${originalCode}`}
            >
              переміщено
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="grid h-7 w-7 place-items-center rounded-md text-text-muted hover:bg-hover hover:text-text-primary transition-colors flex-shrink-0"
          aria-label="Закрити дерево"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex items-center gap-1 border-b border-border-default px-4 h-9 flex-shrink-0" role="tablist" aria-label="Розділи УКТЗЕД">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'tree'}
          onClick={() => setActiveTab('tree')}
          className={`relative inline-flex h-9 items-center gap-1.5 px-2 text-[11px] font-medium transition-colors ${activeTab === 'tree' ? 'text-blue-600 dark:text-blue-400' : 'text-text-muted hover:text-text-primary'}`}
        >
          <FolderTree className="w-3.5 h-3.5" />
          Дерево
          {activeTab === 'tree' && <span className="absolute inset-x-1 bottom-0 h-0.5 rounded-t bg-current" />}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'analytics'}
          onClick={() => setActiveTab('analytics')}
          className={`relative inline-flex h-9 items-center gap-1.5 px-2 text-[11px] font-medium transition-colors ${activeTab === 'analytics' ? 'text-violet-600 dark:text-violet-400' : 'text-text-muted hover:text-text-primary'}`}
        >
          <FileSearch className="w-3.5 h-3.5" />
          Довідка
          {analyticsCount != null && (
            <span className="rounded bg-hover px-1 py-0.5 font-mono text-[9px] tabular-nums text-text-muted">{analyticsCount}</span>
          )}
          {activeTab === 'analytics' && <span className="absolute inset-x-1 bottom-0 h-0.5 rounded-t bg-current" />}
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        <div className={`${activeTab === 'tree' ? 'flex' : 'hidden'} flex-1 min-h-0 flex-col px-4 py-3`}>
          <TariffPanel
            code={code}
            onCountChange={onCountChange}
            onTariffInfo={onTariffInfo}
            onActiveCodeChange={setActiveCode}
          />
        </div>
        {activeTab === 'analytics' && (
          <AnalyticsCardPanel code={activeCode} onCountChange={setAnalyticsCount} />
        )}
      </div>
    </aside>
  );
}
